import { useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Instagram, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import socialVideo1 from "@/assets/social-1.mp4.asset.json";
import socialPoster1 from "@/assets/social-1-poster.jpg.asset.json";
import socialVideo2 from "@/assets/social-2.mp4.asset.json";
import socialPoster2 from "@/assets/social-2-poster.jpg.asset.json";
import socialVideo3 from "@/assets/social-3.mp4.asset.json";
import socialPoster3 from "@/assets/social-3-poster.jpg.asset.json";

type Reel = {
  id: string;
  src?: string;
  poster?: string;
  caption: string;
  link: string;
};

// Voeg hier nieuwe socials-video's toe (src + poster laten vullen)
const reels: Reel[] = [
  {
    id: "reel-1",
    src: socialVideo1.url,
    poster: socialPoster1.url,
    caption: "Wat maakt jouw werk chill? ⚡",
    link: "https://www.instagram.com/chillwerk.nl",
  },
  {
    id: "reel-2",
    src: socialVideo3.url,
    poster: socialPoster3.url,
    caption: "Het gekste wat je ooit meemaakte? 👀",
    link: "https://www.instagram.com/chillwerk.nl",
  },
  {
    id: "reel-3",
    src: socialVideo2.url,
    poster: socialPoster2.url,
    caption: "Wat wilde jij vroeger worden? 💭",
    link: "https://www.tiktok.com/@chillwerk.nl",
  },
];

const ReelCard = ({ reel, featured = false }: { reel: Reel; featured?: boolean }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
    if (v.paused) togglePlay();
  };

  return (
    <div
      className={`group relative transition-transform duration-500 w-[76%] xs:w-[70%] sm:w-[52%] shrink-0 snap-center lg:w-auto lg:shrink lg:snap-align-none ${
        featured ? "lg:-translate-y-4 lg:scale-[1.04] z-10" : "lg:z-0"
      }`}
    >
      {/* Telefoonframe */}
      <div
        className="relative rounded-[2rem] bg-foreground p-2 ring-1 ring-foreground/10"
        style={{ boxShadow: "0 20px 40px rgba(0, 0, 0, 0.08)" }}
      >

      <div className="relative rounded-[1.5rem] overflow-hidden bg-black aspect-[9/16]">

        {reel.src ? (
          <>
            <video
              ref={videoRef}
              src={reel.src}
              poster={reel.poster}
              className="w-full h-full object-cover"
              playsInline
              loop
              muted
              preload="metadata"
              onClick={togglePlay}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            {!isPlaying && (
              <button
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/25 transition-opacity"
                aria-label="Video afspelen"
              >
                <span className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                  <Play className="w-7 h-7 text-foreground ml-1" fill="currentColor" />
                </span>
              </button>
            )}
            <div className="absolute bottom-3 right-3 flex gap-2 z-10">
              <button
                onClick={toggleMute}
                className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                aria-label={isMuted ? "Geluid aan" : "Geluid uit"}
              >
                {isMuted ? <VolumeX className="w-4 h-4 text-foreground" /> : <Volume2 className="w-4 h-4 text-foreground" />}
              </button>
              <button
                onClick={togglePlay}
                className="w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                aria-label={isPlaying ? "Pauzeren" : "Afspelen"}
              >
                {isPlaying ? <Pause className="w-4 h-4 text-foreground" fill="currentColor" /> : <Play className="w-4 h-4 text-foreground ml-0.5" fill="currentColor" />}
              </button>
            </div>
          </>
        ) : (
          <a
            href={reel.link}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/[0.04] border border-dashed border-white/20 text-white/70 hover:bg-white/[0.08] transition-colors"
          >
            <span className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </span>
            <span className="text-sm font-medium">Volg ons voor meer</span>
          </a>
        )}

        {/* Caption */}
        <div className="absolute top-0 inset-x-0 p-4 bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
          <span className="text-white text-sm font-semibold drop-shadow">{reel.caption}</span>
        </div>
      </div>
      </div>
    </div>

  );
};

export const SocialReelsSection = () => {
  return (
    <section className="relative py-16 md:py-24 overflow-hidden bg-background">
      {/* Zachte ambient tints */}
      <div className="absolute top-1/4 left-[8%] w-[26rem] h-[26rem] bg-cyan/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 right-[8%] w-[22rem] h-[22rem] bg-yellow/10 rounded-full blur-[130px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-8 relative z-10">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 bg-yellow text-yellow-foreground px-4 py-1.5 rounded-full font-bold text-sm">
            <Instagram className="w-4 h-4" />
            Straight from onze socials
          </div>
          <h2 className="text-4xl md:text-5xl font-heading font-black text-foreground leading-tight">
            Dit is 
            <span className="relative inline-block">
              <span className="relative z-10">ChillWerk.nl</span>
              <span className="absolute left-0 -bottom-1 w-full h-3 md:h-4 bg-yellow -z-0 rounded-sm" />
            </span>
          </h2>
          <p className="text-lg text-foreground/70">
             Sluit je aan bij de plek waar bedrijven en mensen elkaar ontmoeten.
          </p>
        </div>

        <div className="flex gap-6 sm:gap-8 overflow-x-auto snap-x snap-mandatory scroll-px-4 px-4 -mx-4 pt-2 pb-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden lg:grid lg:grid-cols-3 lg:gap-12 lg:overflow-visible lg:mx-auto lg:px-0 lg:pb-0 lg:max-w-5xl lg:items-center">
          {reels.map((reel, i) => (
            <ReelCard key={reel.id} reel={reel} featured={i === 1} />
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-3 mt-12">
          <Button variant="cta" size="lg" asChild>
            <a href="https://www.instagram.com/chillwerk.nl" target="_blank" rel="noopener noreferrer">
              <Instagram className="w-5 h-5" />
              Volg ons op Instagram
            </a>
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="border-foreground/20 bg-white text-foreground hover:bg-muted"
            asChild
          >
            <a href="https://www.tiktok.com/@chillwerk.nl" target="_blank" rel="noopener noreferrer">
              Bekijk ons op TikTok
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};
